import { ChangeEvent, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { getIsSettingsUpdating, getWorkingProjectUuid } from '@/store/slices/project/selectors';
import Modal from '@ui/modal/modal';
import Text from '@ui/text/text/text';
import Container from '@ui/container/container';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import { updatePublishSettings } from '@/store/slices/project';
import { ChannelFeaturesEnum } from '@/types/feature-flags.types';
import useCanUseChannelFeature from '@/utils/use-can-use-channel-features';
import toastTrigger from '@ui/toast/toast-trigger';
import { selectIsPublishLocked } from '@/store/slices/settings/selectors';
import OptionalComponent from '@ui/optional-component/optional-component';
import Button from '@ui/buttons/button/button';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import Switch from '@ui/switch/switch';

type PublishSettingsModalProps = {
  open: boolean;
  dmaId?: string;
  handleClose: () => void;
};

const PublishSettingsModal = ({ open, dmaId, handleClose }: PublishSettingsModalProps) => {
  const isSaving = useAppSelector(getIsSettingsUpdating);
  const isLocked = useAppSelector(selectIsPublishLocked);
  const projectUuid = useAppSelector(getWorkingProjectUuid);

  const dispatch = useAppDispatch();

  const publishLockEnabled = useCanUseChannelFeature(ChannelFeaturesEnum.event_publish_locking);

  const [localProductId, setLocalProductId] = useState(dmaId);

  const onClose = () => {
    if (isSaving) return;
    handleClose();
  };

  const handlePublishLockToggle = (val: string, on: boolean) => {
    if (projectUuid) {
      dispatch(
        updatePublishSettings({
          project_uuid: projectUuid,
          isLocked: on,
        })
      );
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value;
    const filteredValue = rawValue.replace(/[^0-9.]/g, '');

    if (filteredValue.length <= 9) {
      setLocalProductId(filteredValue);
    }
  };

  const handleSave = () => {
    if (isSaving || !projectUuid) return;
    try {
      if (localProductId && (localProductId.length === 8 || localProductId.length === 9)) {
        const dmaId = localProductId?.trim() || '';

        dispatch(
          updatePublishSettings({
            project_uuid: projectUuid,
            isLocked,
            dmaId,
          })
        );
        handleClose();
      }
    } catch (error) {
      console.error(error);
      toastTrigger(
        <div>
          <div className="S1">Failed to save product ID.</div>
        </div>,
        {
          type: 'error',
          autoClose: 10000,
        }
      );
    }
  };

  useEffect(() => {
    if (open && dmaId) {
      setLocalProductId(dmaId);
    } else {
      setLocalProductId('');
    }
  }, [open, dmaId]);

  return (
    <>
      <Modal
        open={open}
        customSize={{
          height: 'auto',
          minHeight: '200px',
          width: '640px',
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: 'calc(100vh - 64px)',
        }}
        onRequestClose={!isSaving ? handleClose : undefined}
        renderHeaderContent={() => {
          return (
            <Container background="transparent" padding="24px 40px 16px">
              <div className="flex ai-center jc-space-between">
                <Text tag="h4" className="H4 margin-b-4">
                  Publish settings
                </Text>
                <button className="clear-button padding-4 flex ai-center" onClick={!isSaving ? handleClose : undefined}>
                  <Icon name={ICONS.CLOSE} size={16} />
                </button>
              </div>
            </Container>
          );
        }}
        renderFooterContent={() => {
          return (
            <Container
              background="transparent"
              className="flex fd-row jc-flex-end ai-center gap-8"
              padding="0 40px 24px 40px"
            >
              <button onClick={onClose} disabled={isSaving} className="L1 padding-v-16 padding-h-24">
                Cancel
              </button>

              <Button
                backgroundColor={getThemeColorCSSVariable()}
                padding="16px 24px"
                width="auto"
                onClick={handleSave}
                disabled={
                  !localProductId?.trim() ||
                  localProductId?.trim() === dmaId ||
                  localProductId.length < 8 ||
                  localProductId.length > 9 ||
                  isSaving
                }
                disabledOpacity={0.6}
                hoverOpacity={0.8}
                loading={isSaving}
              >
                <Text
                  color={getThemeColorContrastCSSVariable(EColorVariablesV2.primary, EColorVariablesV2.buttonText)}
                  className="L1"
                >
                  Save
                </Text>
              </Button>
            </Container>
          );
        }}
      >
        <Container background="transparent" padding="0 40px 24px 40px" className="flex-column gap-24">
          <div className="flex ai-center jc-space-between height-36">
            <Text className="H5">Publishing</Text>
            <Switch
              value={'publish-lock'}
              on={isLocked}
              onClick={handlePublishLockToggle}
              disabled={!publishLockEnabled}
            />
          </div>
          <div>
            <label className="margin-l-16 P2" color="var(--gray-400)">
              Product ID *
            </label>
            <InputWithOverlay
              inputHeight="40px"
              inputProps={{
                className: 'S2',
                placeholder: 'Enter associated Product ID',
                value: localProductId,
                onChange: handleInputChange,
              }}
            />
            <OptionalComponent display={!!localProductId && localProductId.length < 8}>
              <Text className="P2 position-absolute margin-l-16" color="var(--semantic-red-500)">
                Product ID must be 8 or 9 numbers
              </Text>
            </OptionalComponent>
          </div>
          <div className="flex ai-center gap-12">
            <div>
              <Icon name={ICONS.INFO_CIRCLE} size={16} color="var(--gray-500)" />
            </div>
            <Text className="C2">
              This is a requirement related to data access restrictions. Once you confirm, you will not be able to
              change the Product ID that your event is associated with. For more information visit
              go/marketing-events-pids
            </Text>
          </div>
        </Container>
      </Modal>
    </>
  );
};

export default PublishSettingsModal;
